import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Briefcase, Inbox, Map as MapIcon, Pencil, Plus, Search, Trash2, Upload } from 'lucide-react';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { useAuthStore } from '../store/authStore';
import employerAPI, { type EmployerVacancy } from '../services/employerService';
import { PROFESSIONAL_TYPE_LABELS } from '../types/professionalRates';

const REGIOS = [
  'Noord-Holland', 'Zuid-Holland', 'Utrecht', 'Gelderland', 'Noord-Brabant',
  'Limburg', 'Zeeland', 'Friesland', 'Groningen', 'Drenthe', 'Overijssel', 'Flevoland',
];
const TIERS = ['', 'BRONS', 'ZILVER', 'GOUD'] as const;

const TIER_BADGE: Record<string, string> = {
  GOUD: 'bg-amber-100 text-amber-900 border-amber-300',
  ZILVER: 'bg-slate-100 text-slate-800 border-slate-300',
  BRONS: 'bg-orange-100 text-orange-900 border-orange-300',
};
const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-green-100 text-green-800',
  filled: 'bg-blue-100 text-blue-800',
  closed: 'bg-gray-200 text-gray-600',
};
const ACTIEF = ['draft', 'published'];

type Route = 'zelf' | 'vacatures';
type Weergave = 'actief' | 'historisch' | 'alle';

const selectKlasse = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-navy-900 focus:border-navy-500 focus:ring-navy-500';

/**
 * Dashboard › Professional zoeken: twee hoofdroutes.
 *  A — Zelf zoeken: op criteria (discipline, regio, tier) of op de kaart.
 *  B — Vacature plaatsen: eigen workspace met beheermatrix, reactie-inbox en CRUD.
 */
export const EmployerZoekenPage: React.FC = () => {
  const { t } = useTranslation('employerDashboard');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  useCockpitTitle(t('zoeken.titel'), t('zoeken.ondertitel'));

  const perms = user?.employer_permissions || [];
  const isLid = user?.role === 'employer_member';
  const magZoeken = !isLid || perms.includes('professional_zoeken');
  const magVacatures = !isLid || perms.includes('geplaatste_opdrachten');

  const gevraagd = searchParams.get('route') === 'vacatures' ? 'vacatures' : 'zelf';
  const route: Route = gevraagd === 'zelf' && !magZoeken ? 'vacatures' : gevraagd === 'vacatures' && !magVacatures ? 'zelf' : gevraagd;
  const kiesRoute = (r: Route) => setSearchParams(r === 'zelf' ? {} : { route: r }, { replace: true });

  // Route A — criteria
  const [discipline, setDiscipline] = useState('');
  const [regio, setRegio] = useState('');
  const [tier, setTier] = useState<(typeof TIERS)[number]>('');
  const zoek = () => {
    const p = new URLSearchParams();
    if (discipline) p.set('professional_type', discipline);
    if (regio) p.set('active_regions', JSON.stringify([regio]));
    if (tier) p.set('min_tier', tier);
    navigate(`/employer/dashboard/zoeken/resultaten?${p.toString()}`);
  };
  const disciplines = useMemo(
    () => Object.entries(PROFESSIONAL_TYPE_LABELS).sort(([, a], [, b]) => a.localeCompare(b, 'nl')),
    []
  );

  // Route B — vacature-workspace
  const [weergave, setWeergave] = useState<Weergave>('actief');
  const [verwijderVoor, setVerwijderVoor] = useState<string | null>(null);
  const { data: vacatures = [], isLoading } = useQuery({
    queryKey: ['employer-vacancies'],
    queryFn: async () => (await employerAPI.getVacancies()).data.data ?? [],
    enabled: route === 'vacatures' && magVacatures,
  });
  const zichtbaar = vacatures.filter((v: EmployerVacancy) =>
    weergave === 'alle' ? true : weergave === 'actief' ? ACTIEF.includes(v.status) : !ACTIEF.includes(v.status)
  );
  const verwijder = useMutation({
    mutationFn: (id: string) => employerAPI.deleteVacancy(id),
    onSuccess: () => {
      toast.success(t('zoeken.vacatures.verwijderd'));
      setVerwijderVoor(null);
      queryClient.invalidateQueries({ queryKey: ['employer-vacancies'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('actiepunten.fout')),
  });
  const publiceer = useMutation({
    mutationFn: (id: string) => employerAPI.publishVacancy(id),
    onSuccess: () => {
      toast.success(t('zoeken.vacatures.gepubliceerd'));
      queryClient.invalidateQueries({ queryKey: ['employer-vacancies'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('actiepunten.fout')),
  });

  if (!magZoeken && !magVacatures) {
    return <p className="p-6 text-sm text-navy-600">{t('zoeken.geenToegang')}</p>;
  }

  const routeKnop = (r: Route, titel: string, sub: string, Icon: React.ComponentType<{ className?: string }>, mag: boolean) =>
    mag && (
      <button
        type="button"
        onClick={() => kiesRoute(r)}
        aria-pressed={route === r}
        className={`flex-1 text-left rounded-xl border px-4 py-3 transition-colors ${
          route === r ? 'border-gold-400 bg-gold-50' : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-navy-900">
          <Icon className="w-4 h-4" />
          {titel}
        </span>
        <span className="block text-xs text-navy-500 mt-0.5">{sub}</span>
      </button>
    );

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex flex-col sm:flex-row gap-3">
        {routeKnop('zelf', t('zoeken.routeA'), t('zoeken.routeADesc'), Search, magZoeken)}
        {routeKnop('vacatures', t('zoeken.routeB'), t('zoeken.routeBDesc'), Briefcase, magVacatures)}
      </div>

      {route === 'zelf' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 space-y-3">
            <h2 className="text-sm font-semibold text-navy-900 flex items-center gap-2">
              <Search className="w-4 h-4" />
              {t('zoeken.criteria.titel')}
            </h2>
            <label className="block">
              <span className="text-xs font-medium text-navy-600">{t('zoeken.criteria.discipline')}</span>
              <select className={selectKlasse} value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
                <option value="">{t('zoeken.criteria.alle')}</option>
                {disciplines.map(([waarde, label]) => (
                  <option key={waarde} value={waarde}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-navy-600">{t('zoeken.criteria.regio')}</span>
              <select className={selectKlasse} value={regio} onChange={(e) => setRegio(e.target.value)}>
                <option value="">{t('zoeken.criteria.alle')}</option>
                {REGIOS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-navy-600">{t('zoeken.criteria.tier')}</span>
              <select className={selectKlasse} value={tier} onChange={(e) => setTier(e.target.value as (typeof TIERS)[number])}>
                {TIERS.map((w) => (
                  <option key={w} value={w}>
                    {t(`zoeken.tierOptie.${w || 'ALLE'}`)}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button type="button" onClick={zoek} className="inline-flex items-center gap-1.5 rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800">
                <Search className="w-4 h-4" />
                {t('zoeken.criteria.zoek')}
              </button>
              <Link to="/employer/dashboard/zoeken/uitgebreid" className="text-sm font-medium text-navy-700 hover:text-navy-900">
                {t('zoeken.criteria.uitgebreid')}
              </Link>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 flex flex-col">
            <h2 className="text-sm font-semibold text-navy-900 flex items-center gap-2">
              <MapIcon className="w-4 h-4" />
              {t('zoeken.kaart.titel')}
            </h2>
            <p className="mt-2 text-sm text-navy-600 flex-1">{t('zoeken.kaart.tekst')}</p>
            <div className="pt-3">
              <Link
                to="/employer/dashboard/zoeken/kaart"
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-navy-900 hover:border-gray-400"
              >
                <MapIcon className="w-4 h-4" />
                {t('zoeken.kaart.knop')}
              </Link>
            </div>
          </section>
        </div>
      ) : (
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-navy-900">{t('zoeken.vacatures.matrix')}</h2>
              <div className="inline-flex rounded-lg border border-gray-200 p-0.5">
                {(['actief', 'historisch', 'alle'] as const).map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setWeergave(w)}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold ${weergave === w ? 'bg-navy-900 text-white' : 'text-navy-600 hover:text-navy-900'}`}
                  >
                    {t(`zoeken.vacatures.${w}`)}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/opdrachten/nieuw')}
              className="inline-flex items-center gap-1.5 rounded-lg bg-navy-900 px-3.5 py-2 text-sm font-semibold text-white hover:bg-navy-800"
            >
              <Plus className="w-4 h-4" />
              {t('zoeken.vacatures.nieuw')}
            </button>
          </header>

          {isLoading ? (
            <div className="p-4 space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-10 rounded bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : zichtbaar.length === 0 ? (
            <p className="p-6 text-sm text-navy-500">{t('zoeken.vacatures.leeg')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-navy-400 border-b border-gray-100">
                    <th className="px-4 py-2 font-semibold">{t('zoeken.vacatures.kolomTitel')}</th>
                    <th className="px-4 py-2 font-semibold">{t('zoeken.vacatures.kolomStatus')}</th>
                    <th className="px-4 py-2 font-semibold">{t('zoeken.vacatures.kolomTier')}</th>
                    <th className="px-4 py-2 font-semibold">{t('zoeken.vacatures.kolomGeplaatst')}</th>
                    <th className="px-4 py-2 font-semibold text-right">{t('zoeken.vacatures.kolomActies')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {zichtbaar.map((v: EmployerVacancy) => (
                    <tr key={v.id} className="align-top">
                      <td className="px-4 py-3">
                        <div className="font-medium text-navy-900">{v.titel}</div>
                        <div className="text-xs text-navy-500">
                          {PROFESSIONAL_TYPE_LABELS[v.professional_type as keyof typeof PROFESSIONAL_TYPE_LABELS] ?? v.professional_type}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[v.status] ?? 'bg-gray-100 text-gray-700'}`}>
                          {t(`zoeken.vacatures.status.${v.status}`, v.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {v.min_compliance_tier ? (
                          <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${TIER_BADGE[v.min_compliance_tier]}`}>
                            {t(`zoeken.tierOptie.${v.min_compliance_tier}`)}
                          </span>
                        ) : (
                          <span className="text-xs text-navy-400">{t('zoeken.vacatures.geenEis')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-navy-600 whitespace-nowrap">{new Date(v.created_at).toLocaleDateString('nl-NL')}</td>
                      <td className="px-4 py-3">
                        {verwijderVoor === v.id ? (
                          <div className="flex flex-wrap justify-end items-center gap-2">
                            <span className="text-xs text-navy-600">{t('zoeken.vacatures.verwijderenZeker')}</span>
                            <button
                              type="button"
                              disabled={verwijder.isPending}
                              onClick={() => verwijder.mutate(v.id)}
                              className="rounded-md bg-red-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-red-800 disabled:opacity-50"
                            >
                              {t('zoeken.vacatures.verwijderen')}
                            </button>
                            <button type="button" onClick={() => setVerwijderVoor(null)} className="rounded-md border border-gray-300 px-2.5 py-1 text-xs text-navy-700">
                              {t('actiepunten.BUDGET_VERLENGING.annuleren')}
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Link
                              to={`/employer/dashboard/zoeken/vacatures/${v.id}/sollicitaties`}
                              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-navy-800 hover:border-gray-400"
                            >
                              <Inbox className="w-3.5 h-3.5" />
                              {t('zoeken.vacatures.reacties')}
                            </Link>
                            <Link
                              to={`/employer/dashboard/zoeken/vacatures/${v.id}/bewerken`}
                              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-navy-800 hover:border-gray-400"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                              {t('zoeken.vacatures.wijzigen')}
                            </Link>
                            {v.status === 'draft' && (
                              <button
                                type="button"
                                disabled={publiceer.isPending}
                                onClick={() => publiceer.mutate(v.id)}
                                className="inline-flex items-center gap-1 rounded-md border border-green-300 px-2.5 py-1 text-xs font-medium text-green-800 hover:border-green-500 disabled:opacity-50"
                              >
                                <Upload className="w-3.5 h-3.5" />
                                {t('zoeken.vacatures.publiceren')}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setVerwijderVoor(v.id)}
                              aria-label={t('zoeken.vacatures.verwijderen')}
                              className="inline-flex items-center rounded-md border border-gray-300 px-2 py-1 text-red-700 hover:border-red-400"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
};
